import { useState, useEffect } from 'react'
import styles from './GroupReminderModal.module.css'
import apiClient from '../../utils/api'

const DAYS_OF_WEEK = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
]

export default function GroupReminderModal({ isOpen, onClose, onSave, group, reminder = null, apiToken }) {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        work_capacity_hours: 20,
        enabled: true,
        send_day: 'friday',
        send_time: '14:00',
        notify_admin: true
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Reset form when modal opens/closes or reminder changes
    useEffect(() => {
        if (isOpen) {
            if (reminder) {
                setFormData({
                    name: reminder.name || '',
                    description: reminder.description || '',
                    work_capacity_hours: reminder.work_capacity_hours || 20,
                    enabled: reminder.enabled ?? true,
                    send_day: reminder.send_day || 'friday',
                    send_time: reminder.send_time || '14:00',
                    notify_admin: reminder.notify_admin ?? true
                })
            } else {
                setFormData({
                    name: '',
                    description: '',
                    work_capacity_hours: 20,
                    enabled: true,
                    send_day: 'friday',
                    send_time: '14:00',
                    notify_admin: true
                })
            }
            setError(null)
        }
    }, [isOpen, reminder])

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        try {
            if (!apiToken) {
                throw new Error('Authentication token is required')
            }

            apiClient.setAuthToken(apiToken)

            let result
            if (reminder) {
                // Update existing reminder
                result = await apiClient.updateGroupReminder(group.id, reminder.id, formData)
            } else {
                // Create new reminder
                result = await apiClient.createGroupReminder(group.id, formData)
            }

            if (onSave) {
                onSave(result.reminder)
            }
            onClose()
        } catch (err) {
            console.error('Error saving reminder:', err)
            setError(err.response?.data?.error || err.message || 'Failed to save reminder')
        } finally {
            setLoading(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>{reminder ? 'Edit Reminder' : 'Create Reminder'}</h2>
                    <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.modalForm}>
                    {error && (
                        <div className={styles.error}>
                            {error}
                        </div>
                    )}

                    <div className={styles.formGroup}>
                        <label htmlFor="name">
                            Reminder Name <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="e.g., Weekly capacity check"
                            required
                            maxLength={100}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="description">Description</label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Optional description for this reminder"
                            rows={3}
                            maxLength={500}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="work_capacity_hours">
                            Work Capacity (hours per week) <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="number"
                            id="work_capacity_hours"
                            name="work_capacity_hours"
                            value={formData.work_capacity_hours}
                            onChange={handleChange}
                            min={1}
                            max={168}
                            step={0.5}
                            required
                        />
                        <span className={styles.helpText}>
                            Required hours per week for team members
                        </span>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="send_day">
                                Send Day <span className={styles.required}>*</span>
                            </label>
                            <select
                                id="send_day"
                                name="send_day"
                                value={formData.send_day}
                                onChange={handleChange}
                                required
                            >
                                {DAYS_OF_WEEK.map(day => (
                                    <option key={day.value} value={day.value}>
                                        {day.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.formGroup}>
                            <label htmlFor="send_time">
                                Send Time <span className={styles.required}>*</span>
                            </label>
                            <input
                                type="time"
                                id="send_time"
                                name="send_time"
                                value={formData.send_time}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                name="enabled"
                                checked={formData.enabled}
                                onChange={handleChange}
                            />
                            <span>Enable this reminder</span>
                        </label>
                        <span className={styles.helpText}>
                            Disabled reminders will not send emails
                        </span>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                name="notify_admin"
                                checked={formData.notify_admin}
                                onChange={handleChange}
                            />
                            <span>Send summary email to admins</span>
                        </label>
                        <span className={styles.helpText}>
                            Admins will receive a summary of all users below capacity
                        </span>
                    </div>

                    <div className={styles.modalActions}>
                        <button
                            type="button"
                            className={styles.cancelButton}
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : (reminder ? 'Update Reminder' : 'Create Reminder')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}
